import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'cardVariant',
})(({ theme, cardVariant }) => {
  const variants = {
    default: {
      borderRadius: 0,
      transition: 'all 0.2s ease',
      '&:hover': {
        boxShadow: `0 4px 12px ${theme.palette.primary.main}15`,
      },
    },
    stats: {
      background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
      backdropFilter: 'blur(20px)',
      border: `1px solid ${theme.palette.primary[100]}`,
      borderRadius: theme.spacing(2),
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 12px 40px ${theme.palette.primary.main}15`,
      },
    },
    info: {
      borderRadius: 0,
      backgroundColor: theme.palette.primary[50] || `${theme.palette.primary.main}08`,
      border: `1px solid ${theme.palette.primary[200] || theme.palette.primary.light}`,
      transition: 'all 0.2s ease',
    },
    action: {
      borderRadius: 0,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 8px 25px ${theme.palette.primary.main}20`,
        backgroundColor: theme.palette.primary[25] || `${theme.palette.primary.main}04`,
      },
    },
    elevated: {
      borderRadius: 0,
      boxShadow: `0 4px 12px ${theme.palette.primary.main}10`,
      border: `1px solid ${theme.palette.divider}`,
      transition: 'all 0.2s ease',
      '&:hover': {
        boxShadow: `0 8px 25px ${theme.palette.primary.main}20`,
      },
    },
    upload: {
      borderRadius: 0,
      border: `2px dashed ${theme.palette.primary[300] || theme.palette.primary.light}`,
      backgroundColor: theme.palette.primary[25] || `${theme.palette.primary.main}04`,
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.primary[50] || `${theme.palette.primary.main}08`,
        transform: 'scale(1.02)',
      },
    },
  };

  return variants[cardVariant] || variants.default;
});

const EasyCheckCard = ({
  children,
  variant = 'default',
  title,
  subtitle,
  icon,
  value,
  onClick,
  ...props
}) => {
  const isStatsCard = variant === 'stats' && (title || value);

  return (
    <StyledCard
      cardVariant={variant}
      onClick={onClick}
      {...props}
    >
      <CardContent>
        {isStatsCard ? (
          <Box>
            {icon && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {icon}
              </Box>
            )}
            {value && (
              <Typography variant="h4" fontWeight={700} color="primary" sx={{ mb: 1 }}>
                {value}
              </Typography>
            )}
            {title && (
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.disabled">
                {subtitle}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {(title || subtitle || icon) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: title || subtitle ? 2 : 0 }}>
                {icon}
                <Box>
                  {title && (
                    <Typography variant="h6" fontWeight={600}>
                      {title}
                    </Typography>
                  )}
                  {subtitle && (
                    <Typography variant="body2" color="text.secondary">
                      {subtitle}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            {children}
          </>
        )}
      </CardContent>
    </StyledCard>
  );
};

EasyCheckCard.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'stats', 'info', 'action', 'elevated', 'upload']),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  icon: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClick: PropTypes.func,
};

export default EasyCheckCard; 